import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Check, Info } from 'lucide-react'
import { cn } from '../../../utils/cn'

interface AssessmentModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  type: 'review' | 'great-work' | 'made-start'
  onPrimaryAction?: () => void
  onSecondaryAction?: () => void
  skippedQuestionsCount?: number
  className?: string
}

export function AssessmentModal({
  open,
  onOpenChange,
  type,
  onPrimaryAction,
  onSecondaryAction,
  skippedQuestionsCount = 0,
  className,
}: AssessmentModalProps) {
  const renderContent = () => {
    switch (type) {
      case 'review':
        return (
          <>
            <div className='flex flex-col items-center space-y-6 py-8'>
              {/* Icon */}
              <div className='flex h-16 w-16 items-center justify-center rounded-2xl bg-green-500'>
                <Check className='h-8 w-8 text-white' strokeWidth={3} />
              </div>

              {/* Title */}
              <DialogTitle className='text-2xl font-bold text-gray-900'>
                Review Your Assessment
              </DialogTitle>

              {/* Description */}
              <p className='max-w-md text-center text-gray-600 leading-relaxed'>
                Well done! You have reached the end. You can review your
                answers, go back to skipped questions, or submit now.
              </p>

              {/* Actions */}
              <div className='flex w-full max-w-sm flex-col gap-3'>
                <Button
                  onClick={onPrimaryAction}
                  variant='outline'
                  className='w-full rounded-lg border-gray-300 py-6 text-base font-medium bg-transparent'
                >
                  Review Assessment
                </Button>
                {skippedQuestionsCount > 0 && (
                  <Button
                    onClick={onSecondaryAction}
                    variant='outline'
                    className='w-full rounded-lg border-gray-300 py-6 text-base font-medium bg-transparent'
                  >
                    Go to Skipped Questions ({skippedQuestionsCount})
                  </Button>
                )}
                <Button
                  onClick={onPrimaryAction}
                  disabled={skippedQuestionsCount > 0}
                  className={cn(
                    'w-full rounded-lg py-6 text-base font-medium text-white',
                    skippedQuestionsCount > 0
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-gray-900 hover:bg-gray-800'
                  )}
                >
                  Submit Assessment
                </Button>
              </div>
            </div>
          </>
        )

      case 'great-work':
        return (
          <>
            <div className='flex flex-col items-center space-y-6 py-8'>
              {/* Icon */}
              <div className='text-6xl'>💪</div>

              {/* Title */}
              <DialogTitle className='text-3xl font-bold text-gray-900'>
                Great Work!
              </DialogTitle>

              {/* Description */}
              <p className='max-w-lg text-center text-gray-600 leading-relaxed'>
                Your score shows strong foundations in your relationship. Keep
                building on this by continuing your learning path and applying
                what you have learned together.
              </p>

              {/* Action */}
              <Button
                onClick={onPrimaryAction}
                className='w-full max-w-xs rounded-lg bg-gray-900 py-6 text-base font-medium text-white hover:bg-gray-800'
              >
                Proceed
              </Button>
            </div>
          </>
        )

      case 'made-start':
        return (
          <>
            <div className='flex flex-col items-center space-y-6 py-8'>
              {/* Info Icon */}
              <div className='flex h-12 w-12 items-center justify-center rounded-full bg-blue-500'>
                <Info className='h-6 w-6 text-white' />
              </div>

              {/* Icon */}
              <div className='text-6xl'>🚀</div>

              {/* Title */}
              <DialogTitle className='text-3xl font-bold text-gray-900'>
                You have Made a Start!
              </DialogTitle>

              {/* Description */}
              <p className='max-w-lg text-center text-gray-600 leading-relaxed'>
                Your score shows there are areas where growth is needed — and
                that is okay. Everyone begins somewhere. Watch the recommended
                training to strengthen your relationship step by step.
              </p>

              {/* Action */}
              <Button
                onClick={onPrimaryAction}
                className='w-full max-w-xs rounded-lg bg-gray-900 py-6 text-base font-medium text-white hover:bg-gray-800'
              >
                Start Session
              </Button>
            </div>
          </>
        )
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('max-w-2xl rounded-3xl p-8', className)}>
        <DialogHeader className='sr-only'>
          <DialogTitle>Assessment Modal</DialogTitle>
        </DialogHeader>
        {renderContent()}
      </DialogContent>
    </Dialog>
  )
}
